import { useEffect, useRef, useState, useCallback } from 'react';
import Phaser from 'phaser';
import { AlienAttackScene } from './AlienAttackScene';

interface AlienAttackGameProps {
  onScoreChange?: (score: number) => void;
  onGameOver?: (score: number) => void;
}

export function AlienAttackGame({
  onScoreChange,
  onGameOver,
}: AlienAttackGameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleScoreChange = useCallback(
    (score: number) => {
      onScoreChange?.(score);
    },
    [onScoreChange]
  );

  const handleGameOver = useCallback(
    (score: number) => {
      onGameOver?.(score);
    },
    [onGameOver]
  );

  useEffect(() => {
    if (!containerRef.current || gameRef.current) return;

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: containerRef.current,
      width: 800,
      height: 600,
      backgroundColor: '#000000',
      scene: AlienAttackScene,
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
    };

    gameRef.current = new Phaser.Game(config);

    gameRef.current.events.once('ready', () => {
      setIsLoading(false);
      const scene = gameRef.current?.scene.getScene(
        'AlienAttackScene'
      ) as AlienAttackScene;
      if (scene) {
        scene.scene.restart({
          attractMode: false,
          onScoreChange: handleScoreChange,
          onGameOver: handleGameOver,
        });
      }
    });

    return () => {
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, [handleScoreChange, handleGameOver]);

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-white text-xl">Loading...</div>
        </div>
      )}
      <div
        ref={containerRef}
        className="w-full h-full"
        style={{ minHeight: '400px' }}
      />
    </div>
  );
}
